import { useCallback, useEffect } from 'react'

import { useFieldArray, useFormContext, type FieldValues, type Control } from 'react-hook-form'

import { DistribuidorPercentual } from '@/features/proposta/entities/ParticipacaoComprador/distribuicao-participacao'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import { Cnpj } from '@/utils/cnpj'
import { Cpf } from '@/utils/cpf'
import { resolvePathWithQuery } from '@/utils/object.utils'

const TOTAL_PERCENTUAIS_EM_CENTESIMOS = 10_000

type UseParticipacaoCompradoresConfig = {
  basePath: string
  compradores: TCompradoresCadastrados
}

type TCompradorParticipacao = { id: string; percentualParticipacao: number }

type TFieldWithId = TCompradorParticipacao & { idField: string }

type UseParticipacaoCompradoresReturn = {
  titularId: string
  titularPercentual: number
  compradoresNaoTitular: TCompradorParticipacao[]
  cadastroCompradores: Array<TCompradoresCadastrados[number] & { label: string }>
  resumoDistribuicao: ReturnType<typeof DistribuidorPercentual.obterResumoParticipacao>
  isCompleted: boolean
  fields: TFieldWithId[]
  distribuirParticipacoes: () => void
  handleCompradorPercentualBlur: () => void
  control: Control<FieldValues>
  trigger: (name?: string | string[]) => Promise<boolean>
  paths: {
    titularId: string
    titularPercentual: string
    compradores: string
    titular: string
  }
}

export function useParticipacaoCompradores({
  basePath,
  compradores,
}: UseParticipacaoCompradoresConfig): UseParticipacaoCompradoresReturn {
  const {
    control,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext()

  const paths = {
    titularId: `${basePath}.titular.id`,
    titularPercentual: `${basePath}.titular.percentualParticipacao`,
    compradores: `${basePath}.compradores`,
    titular: `${basePath}.titular`,
  }

  const cadastroCompradores = compradores
    .map((comprador) => ({
      ...comprador,
      label: `${comprador.nome} - ${comprador.tipo === 'PessoaFisica' ? new Cpf(comprador.documento).format() : new Cnpj(comprador.documento).format()}`,
    }))
    .sort((a, b) => a.nome.localeCompare(b.nome))

  const titularId = watch(paths.titularId) as string
  const titularPercentual = watch(paths.titularPercentual) as number
  const compradoresNaoTitular = (watch(paths.compradores) ?? []) as TCompradorParticipacao[]

  const compradoresFiltrados = useCallback(() => {
    const titularIdAtualizado = getValues(paths.titularId)
    return cadastroCompradores?.filter((c) => c.id !== titularIdAtualizado)
  }, [cadastroCompradores, getValues, paths.titularId])

  const { fields, replace } = useFieldArray({
    control,
    name: paths.compradores,
    keyName: 'idField',
  })

  const handleLimparParticipacoesQuandoAlteradoCompradores = useCallback(() => {
    const titularIdAtualizado = getValues(paths.titularId)
    const compradoresIds = new Set(cadastroCompradores.map((c) => c.id))
    const currentCompradores = (getValues(paths.compradores) ?? []) as TCompradorParticipacao[]
    const preenchidosIds = new Set([titularIdAtualizado, ...currentCompradores.map((c) => c.id)])

    const listasSaoIguais =
      compradoresIds.size === preenchidosIds.size && [...compradoresIds].every((id) => preenchidosIds.has(id))

    if (!listasSaoIguais) {
      setValue(paths.titular, { id: '', percentualParticipacao: 0 })
      replace([])
    }
  }, [cadastroCompradores, getValues, paths.titularId, paths.compradores, paths.titular, setValue, replace])

  function gerarDistribuicaoParticipacao(titularPct: number, compradorSimplificado: TCompradoresCadastrados) {
    const percentualTitularEmCentesimos = Math.round(titularPct * 100)
    const percentualRestanteEmCentesimos = TOTAL_PERCENTUAIS_EM_CENTESIMOS - percentualTitularEmCentesimos
    const percentualRestante = percentualRestanteEmCentesimos / 100
    return new DistribuidorPercentual(percentualRestante).distribuir(compradorSimplificado.map((c) => c.id))
  }

  const distribuirParticipacoes = useCallback(() => {
    const percentualTitularAtualizado = (getValues(paths.titularPercentual) ?? 0) as number
    const compradoresRestantes = compradoresFiltrados()
    const compradoresComPercentualDistribuido = gerarDistribuicaoParticipacao(
      percentualTitularAtualizado,
      compradoresRestantes,
    )
    replace(compradoresComPercentualDistribuido)
    trigger(paths.compradores)
  }, [compradoresFiltrados, getValues, paths.titularPercentual, paths.compradores, replace, trigger])

  const handleCompradorPercentualBlur = useCallback(() => {
    trigger(basePath)
  }, [basePath, trigger])

  useEffect(() => {
    handleLimparParticipacoesQuandoAlteradoCompradores()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const participacaoCompradoresErrors = resolvePathWithQuery(errors, basePath)
  const isCompleted = !!titularId && !!titularPercentual && !participacaoCompradoresErrors

  const resumoDistribuicao = DistribuidorPercentual.obterResumoParticipacao(titularPercentual, compradoresNaoTitular)

  return {
    titularId,
    titularPercentual,
    compradoresNaoTitular,
    cadastroCompradores,
    resumoDistribuicao,
    isCompleted,
    fields: fields as TFieldWithId[],
    distribuirParticipacoes,
    handleCompradorPercentualBlur,
    control,
    trigger,
    paths,
  }
}

export type { UseParticipacaoCompradoresReturn }
